import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DateUtil } from '@core/date/util/date-util';
import { FileReference } from '@core/file/models/file-reference.interface';
import { EnvironmentLoaderService } from 'ok-angular-shared/services';
import { toSnakeCase } from 'ok-angular-shared/utils';
import { map, Observable } from 'rxjs';
import { AnswerScreeningDto } from 'src/app/installation-info/models/answer-screening-dto.interface';
import { UpdateScreeningAnswersDto } from 'src/app/installation-info/models/update-screening-answers-dto.interface';
import { AppSettings } from 'src/settings/appsettings.interface';
import {
  AssignTeamMemberRequestDto,
  AssignToSubcontractorRequestDto,
  ChangeScreeningextContactDateRequestDto,
  ChangeSubcontractorRequestDto,
  FinalizeScreeningRequestDto,
  ResendOrderConfirmationEmailRequestDto,
  ResendOrderEmailAddressDetails,
  ScreeningPaginatedDto,
  ScreeningSearchEvent,
  UploadFileResponseDto,
} from '../model';
import { ScreeningDetailsResponseDto } from '../screening-details/models/screening-details-response-dto.interface';
import { mapScreeningExpandedStatusEnum } from '../util/screening-expanded-status-enum-mapper';
import { EditInstallationDescriptionRequestDto } from './models/edit-installation-description-request-dto.interface';
import { GetInstallationDescriptionResponseDto } from './models/get-installation-description-response-dto.interface';
import { ScreeningAnswersForEditResponseDto } from './models/screening-answers-for-edit-response-dto.interface';
import { ScreeningFileReferenceType } from './models/screening-file-reference-type.interface';
import { ScreeningQueryParamsEnum } from './models/screening-query-params.enum';
import { ScreeningValidationResponseDto } from './models/screening-validation-response-dto.interface';
import { SubcontractorsResponseDto } from './models/subcontractors-response-dto.interface';

@Injectable({
  providedIn: 'root',
})
export class ScreeningService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject<EnvironmentLoaderService<AppSettings>>(EnvironmentLoaderService);

  private apiURL: string;
  private apiUrlPlural: string;
  private subcontractorsApiUrl: string;

  constructor() {
    this.apiURL = this.environmentLoaderService.config.subscriptionServiceBaseUrl + '/api/v1/Screening';
    this.apiUrlPlural = this.environmentLoaderService.config.subscriptionServiceBaseUrl + '/api/v1/screenings';
    this.subcontractorsApiUrl = this.environmentLoaderService.config.subscriptionServiceBaseUrl + '/api/v1/subcontractors';
  }

  public verifyGuid(guid: string): Observable<ScreeningValidationResponseDto> {
    const url = `${this.apiURL}/${guid}/validate`;

    return this.http.get<ScreeningValidationResponseDto>(url);
  }

  public submit(guid: string, answer: AnswerScreeningDto) {
    const url = `${this.apiUrlPlural}/${guid}/answer`;
    return this.http.post<AnswerScreeningDto>(url, toSnakeCase(answer));
  }

  public getScreening(id: number): Observable<ScreeningDetailsResponseDto> {
    const url = this.apiUrlPlural + '/' + id;
    return this.http.get<ScreeningDetailsResponseDto>(url).pipe(map(response => this.mapGetScreeningResponseDto(response)));
  }

  public uploadFile(identifier: string, file: FileReference, type: ScreeningFileReferenceType): Observable<UploadFileResponseDto> {
    const url = `${this.apiURL}/${identifier}/file/${type}`;
    const formdata = new FormData();

    formdata.append('formFile', file.fileContent);
    return this.http.post<UploadFileResponseDto>(url, formdata);
  }

  public getScreenings(searchEvent?: ScreeningSearchEvent): Observable<ScreeningPaginatedDto> {
    let params = new HttpParams();

    if (searchEvent?.model?.searchQuery) {
      params = params.append('q', searchEvent.model.searchQuery);
    }

    if (searchEvent?.status) {
      params = params.append(ScreeningQueryParamsEnum.status, searchEvent.status.toString());
    }

    if (searchEvent?.assignedToUserId) {
      params = params.append(ScreeningQueryParamsEnum.assignedTo, searchEvent.assignedToUserId);
    }

    if (searchEvent?.includeInstalled) {
      params = params.append(ScreeningQueryParamsEnum.includeInstalled, searchEvent.includeInstalled);
    }

    if (searchEvent?.includeFutureContactDate) {
      params = params.append(ScreeningQueryParamsEnum.includeFutureContactDate, searchEvent.includeFutureContactDate);
    }

    if (searchEvent?.pageIndex) {
      params = params.append(ScreeningQueryParamsEnum.page, searchEvent.pageIndex);
    }

    if (searchEvent?.pageSize) {
      params = params.append(ScreeningQueryParamsEnum.pageSize, searchEvent.pageSize);
    }

    if (searchEvent?.sort?.active) {
      params = params.append(ScreeningQueryParamsEnum.sortBy, searchEvent.sort.active);
      params = params.append(ScreeningQueryParamsEnum.sortDirection, searchEvent.sort.direction);
    }

    const options = {
      params: params,
    };

    return this.http
      .get<ScreeningPaginatedDto>(this.apiUrlPlural, options)
      .pipe(map(response => this.mapGetScreeningsResponseDto(response)));
  }

  public getSubcontractors(): Observable<SubcontractorsResponseDto> {
    const url = this.subcontractorsApiUrl;
    return this.http.get<SubcontractorsResponseDto>(url);
  }

  public assignToSubcontractor(screeningId: number, request: AssignToSubcontractorRequestDto) {
    const url = this.apiUrlPlural + '/' + screeningId + '/assign-to-subcontractor';
    return this.http.post<void>(url, toSnakeCase(request));
  }

  public changeSubcontractor(screeningId: number, request: ChangeSubcontractorRequestDto) {
    const url = this.apiUrlPlural + '/' + screeningId + '/change-subcontractor';
    return this.http.post<void>(url, toSnakeCase(request));
  }

  public getOrderConfirmationEmailAddressForResend(id: number): Observable<ResendOrderEmailAddressDetails> {
    const url = this.apiURL + '/' + id + '/GetOrderConfirmationEmailAddressForResend';
    return this.http.get<ResendOrderEmailAddressDetails>(url);
  }

  public resendOrderConfirmationEmail(id: number, request: ResendOrderConfirmationEmailRequestDto) {
    const url = this.apiURL + '/' + id + '/ResendOrderConfirmationEmail';
    return this.http.post(url, request);
  }

  public stopReminderEmails(id: number) {
    const url = this.apiURL + '/' + id + '/StopReminders';
    return this.http.put(url, null);
  }

  public finalizeScreening(id: number, expectedInstallationDate: Date) {
    const url = this.apiURL + '/' + id + '/FinalizeScreening';
    const request: FinalizeScreeningRequestDto = {
      expectedInstallationDate: expectedInstallationDate,
    };

    return this.http.post(url, request);
  }

  public getScreeningAnswersForEdit(id: number): Observable<ScreeningAnswersForEditResponseDto> {
    const url = `${this.apiUrlPlural}/${id}/answers-for-edit`;
    return this.http.get<ScreeningAnswersForEditResponseDto>(url);
  }

  public updateScreeningAnswers(id: number, updatedAnswers: UpdateScreeningAnswersDto) {
    const url = `${this.apiUrlPlural}/${id}/update-answers`;
    return this.http.put<UpdateScreeningAnswersDto>(url, toSnakeCase(updatedAnswers));
  }

  public deleteScreeningFile(identifier: string, fileId: number) {
    const url = `${this.apiURL}/${identifier}/file/${fileId}`;
    return this.http.delete(url);
  }

  public sendCancelWarning(id: number) {
    const url = `${this.apiURL}/${id}/SendCancelWarning`;
    return this.http.post<void>(url, null);
  }

  public getScreeningTeamMembers() {
    const url = this.apiURL + '/' + 'teamMembers';
    return this.http.get<string[]>(url);
  }

  public assignScreeningTeamMemberToScreening(id: number, screeningTeamMember: string) {
    const url = `${this.apiURL}/${id}/assignScreeningToTeamMember`;
    const request: AssignTeamMemberRequestDto = {
      teamMemberName: screeningTeamMember,
    };
    return this.http.post(url, request);
  }

  public removeAssignedScreeningTeamMember(id: number) {
    const url = `${this.apiURL}/${id}/removeAssignedTeamMember`;
    return this.http.delete(url);
  }

  public editInstallationDescription(id: number, dto: EditInstallationDescriptionRequestDto) {
    const url = `${this.apiURL}/${id}/EditInstallationDescription`;
    return this.http.put<void>(url, dto);
  }

  public getInstallationDescription(id: number): Observable<GetInstallationDescriptionResponseDto> {
    const url = `${this.apiURL}/${id}/InstallationDescription`;
    return this.http.get<GetInstallationDescriptionResponseDto>(url);
  }

  public changeNextContactDate(screeningId: number, nextContactDate: Date) {
    const url = `${this.apiUrlPlural}/${screeningId}/next-contact-date`;

    const request: ChangeScreeningextContactDateRequestDto = {
      nextContactDate: DateUtil.toDateOnlyString(nextContactDate),
    };

    return this.http.put<void>(url, toSnakeCase(request));
  }

  private mapGetScreeningResponseDto(response: ScreeningDetailsResponseDto): ScreeningDetailsResponseDto {
    response.status = mapScreeningExpandedStatusEnum(response.status);
    response.additionalOrders?.map(order => {
      order.status = mapScreeningExpandedStatusEnum(order.status);
    });
    return response;
  }

  private mapGetScreeningsResponseDto(response: ScreeningPaginatedDto): ScreeningPaginatedDto {
    response.items?.map(item => {
      item.status = mapScreeningExpandedStatusEnum(item.status);
    });
    return response;
  }
}
